import { useRef, useState } from 'react'
import type { DomainEvent } from '@domain/events.ts'
import type { DayKind } from '@domain/calendar.ts'
import { guessKind, mergeRanges, parseIcs, type IcsRange } from '@adapters/ics/parse.ts'
import { missingHolidays } from '@domain/rules/festivos.ts'
import { es } from '../strings/es.ts'

/**
 * Subir el calendario escolar.
 *
 * El fichero **no sale de la tablet**: se lee aquí, con `FileReader`, y lo que
 * queda son unos eventos en el registro. Es la única de las formas que se
 * barajaron de traer el calendario que no añade una dependencia de red a una
 * app que vive en modo avión, y de paso la única que sigue funcionando el día
 * que la consejería cambie su web o esta familia cambie de comunidad.
 *
 * La forma es la misma que la del ayudante, y por la misma razón: **propone, un
 * adulto lee, un adulto aplica**. Aquí importa incluso más que con la IA,
 * porque el fallo es invisible. Un día lectivo marcado como festivo son quince
 * minutos más de tablet y nadie lo nota nunca — así que lo que el fichero no
 * dice claramente llega sin marcar, y se queda fuera salvo que alguien lo
 * decida a mano.
 */

const TIPOS: readonly DayKind[] = ['vacation', 'holiday', 'special', 'weekday']

interface Fila {
  readonly rango: IcsRange
  readonly kind: DayKind | undefined
}

/** «2026-12-20» a «20/12/2026», que es como se lee una fecha aquí. */
function enCorto(dia: string): string {
  const [y, m, d] = dia.split('-')
  return `${d}/${m}/${y}`
}

export function Calendario({
  events,
  onImport,
  onBack,
}: {
  /** El registro, para poder enseñar lo que ya está puesto. */
  events: readonly DomainEvent[]
  onImport: (rangos: readonly { from: string; to: string; kind: DayKind; note: string }[]) => void
  onBack: () => void
}) {
  const [filas, setFilas] = useState<Fila[] | null>(null)
  const [nota, setNota] = useState<string | null>(null)
  const entrada = useRef<HTMLInputElement>(null)

  // Un tramo a mano, para lo que el fichero del colegio no trae: el verano, el
  // fin de curso, un puente que se monta sobre la marcha.
  const [desde, setDesde] = useState('')
  const [hasta, setHasta] = useState('')
  const [tipoMano, setTipoMano] = useState<DayKind>('vacation')
  const [nombreMano, setNombreMano] = useState('')

  /** Lo ya declarado, leído del registro, del más reciente al más viejo. */
  const puestos = events
    .filter((e) => e.type === 'DayRangeDeclared')
    .slice(-30)
    .reverse()

  const ponerAMano = (): void => {
    if (desde === '' || hasta === '') {
      setNota(es.calendario.manoFechas)
      return
    }
    if (hasta < desde) {
      setNota(es.calendario.manoAlReves)
      return
    }
    onImport([
      { from: desde, to: hasta, kind: tipoMano, note: nombreMano.trim() || es.days[tipoMano] },
    ])
    setNota(es.calendario.importado(1))
    setDesde('')
    setHasta('')
    setNombreMano('')
  }

  const leer = async (file: File): Promise<void> => {
    setNota(null)
    const texto = await file.text()
    const rangos = mergeRanges(parseIcs(texto))
    if (rangos.length === 0) {
      setFilas(null)
      setNota(es.calendario.vacio)
      return
    }
    setFilas(rangos.map((rango) => ({ rango, kind: guessKind(rango.summary) })))
  }

  // `flatMap` en vez de `filter` para que el tipo se estreche de verdad: lo que
  // sale ya tiene `kind`, sin aserciones que el linter prohíbe con razón.
  const elegidas: { rango: IcsRange; kind: DayKind }[] = (filas ?? []).flatMap((f) =>
    f.kind === undefined ? [] : [{ rango: f.rango, kind: f.kind }],
  )

  /**
   * La respuesta a «¿este fichero trae la Navidad?», dada por la app en vez de
   * por una suposición. Si el calendario del colegio ya los trae, esta lista
   * sale vacía y no se enseña nada.
   */
  const faltan = filas === null ? [] : missingHolidays(elegidas.map((e) => e.rango))

  const aplicar = (): void => {
    onImport(
      elegidas.map((f) => ({
        from: f.rango.from,
        to: f.rango.to,
        kind: f.kind,
        note: f.rango.summary,
      })),
    )
    setFilas(null)
    setNota(es.calendario.importado(elegidas.length))
  }

  return (
    <div className="mx-auto max-w-2xl px-5 pt-5 pb-16">
      <button type="button" onClick={onBack} className="text-muted mb-5 text-sm font-bold">
        {es.today.back}
      </button>
      <h1 className="mb-2 text-2xl font-extrabold tracking-tight">📅 {es.calendario.title}</h1>
      <p className="text-muted mt-0 mb-1 text-sm">{es.calendario.intro}</p>
      <p className="text-muted mt-0 mb-5 text-sm">{es.calendario.privacidad}</p>

      <button
        type="button"
        onClick={() => entrada.current?.click()}
        className="border-rule w-full rounded-xl border-2 border-dashed px-6 py-5 font-bold"
      >
        + {es.calendario.elegir}
      </button>
      <input
        ref={entrada}
        type="file"
        accept=".ics,text/calendar"
        className="hidden"
        aria-label={es.calendario.elegir}
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) void leer(file)
          e.target.value = ''
        }}
      />

      {nota ? (
        <p role="status" className="bg-teal-soft text-teal mt-4 rounded-lg p-3 font-bold">
          {nota}
        </p>
      ) : null}

      {filas && filas.length > 0 ? (
        <section className="mt-8">
          <h2 className="mb-1 text-lg font-extrabold">{es.calendario.encontrado(filas.length)}</h2>
          <p className="text-muted mt-0 mb-4 text-sm">{es.calendario.revisa}</p>

          <ul className="grid list-none gap-3 p-0">
            {filas.map((fila, index) => (
              <li
                key={`${fila.rango.from}-${String(index)}`}
                className={`border-rule m-0 rounded-xl border-2 p-4 ${
                  fila.kind === undefined ? '' : 'bg-surface'
                }`}
              >
                <p className="m-0 font-bold">{fila.rango.summary}</p>
                <p className="text-muted m-0 mt-1 text-sm">
                  {fila.rango.from === fila.rango.to
                    ? enCorto(fila.rango.from)
                    : es.calendario.rango(enCorto(fila.rango.from), enCorto(fila.rango.to))}
                </p>

                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    aria-pressed={fila.kind === undefined}
                    onClick={() => {
                      setFilas(filas.map((f, i) => (i === index ? { ...f, kind: undefined } : f)))
                    }}
                    className={`border-rule rounded-lg border-2 px-3 py-2 text-sm font-bold ${
                      fila.kind === undefined ? 'bg-coral-soft text-coral border-coral' : ''
                    }`}
                  >
                    {es.calendario.fuera}
                  </button>
                  {TIPOS.map((tipo) => (
                    <button
                      key={tipo}
                      type="button"
                      aria-pressed={fila.kind === tipo}
                      onClick={() => {
                        setFilas(filas.map((f, i) => (i === index ? { ...f, kind: tipo } : f)))
                      }}
                      className={`border-rule rounded-lg border-2 px-3 py-2 text-sm font-bold ${
                        fila.kind === tipo ? 'bg-teal-soft text-teal border-teal' : ''
                      }`}
                    >
                      {es.days[tipo]}
                    </button>
                  ))}
                </div>
              </li>
            ))}
          </ul>

          {faltan.length > 0 ? (
            <section className="border-gold bg-gold-soft mt-6 rounded-xl border-t-4 p-4">
              <p className="m-0 mb-1 font-extrabold">{es.calendario.faltanTitulo}</p>
              <p className="text-muted m-0 mb-3 text-sm">{es.calendario.faltanAyuda}</p>
              <ul className="m-0 mb-3 grid list-none gap-1 p-0 text-sm">
                {faltan.map((festivo) => (
                  <li key={festivo.day} className="m-0">
                    {enCorto(festivo.day)} · {festivo.name}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => {
                  const anadidos: Fila[] = faltan.map((festivo) => ({
                    rango: { summary: festivo.name, from: festivo.day, to: festivo.day },
                    kind: 'holiday',
                  }))
                  setFilas([...(filas ?? []), ...anadidos])
                }}
                className="border-rule rounded-lg border-2 px-5 py-3 font-bold"
              >
                {es.calendario.faltanAnadir(faltan.length)}
              </button>
            </section>
          ) : null}

          <button
            type="button"
            disabled={elegidas.length === 0}
            onClick={aplicar}
            className="bg-teal mt-6 w-full rounded-xl px-6 py-4 text-lg font-extrabold text-white disabled:opacity-40"
          >
            {es.calendario.aplicar(elegidas.length)}
          </button>
        </section>
      ) : null}

      <section className="border-rule mt-10 border-t pt-6">
        <h2 className="mb-1 text-lg font-extrabold">{es.calendario.manoTitulo}</h2>
        <p className="text-muted mt-0 mb-4 text-sm">{es.calendario.manoAyuda}</p>

        <div className="mb-3 flex flex-wrap gap-3">
          <label className="text-sm font-bold">
            {es.calendario.manoDesde}
            <input
              type="date"
              value={desde}
              onChange={(e) => {
                setDesde(e.target.value)
              }}
              className="border-rule bg-surface mt-1 block rounded-lg border-2 p-2"
            />
          </label>
          <label className="text-sm font-bold">
            {es.calendario.manoHasta}
            <input
              type="date"
              value={hasta}
              onChange={(e) => {
                setHasta(e.target.value)
              }}
              className="border-rule bg-surface mt-1 block rounded-lg border-2 p-2"
            />
          </label>
        </div>

        <label className="mb-3 block text-sm font-bold">
          {es.calendario.manoNombre}
          <input
            value={nombreMano}
            onChange={(e) => {
              setNombreMano(e.target.value)
            }}
            className="border-rule bg-surface mt-1 block w-full rounded-lg border-2 p-2"
          />
        </label>

        <div className="mb-4 flex flex-wrap gap-2">
          {TIPOS.map((tipo) => (
            <button
              key={tipo}
              type="button"
              aria-pressed={tipoMano === tipo}
              onClick={() => {
                setTipoMano(tipo)
              }}
              className={`border-rule rounded-lg border-2 px-3 py-2 text-sm font-bold ${
                tipoMano === tipo ? 'bg-teal-soft text-teal border-teal' : ''
              }`}
            >
              {es.days[tipo]}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={ponerAMano}
          className="bg-teal rounded-lg px-5 py-3 font-bold text-white"
        >
          {es.calendario.manoPoner}
        </button>
      </section>

      <section className="border-rule mt-8 border-t pt-6">
        <h2 className="mb-1 text-lg font-extrabold">{es.calendario.puestoTitulo}</h2>
        {puestos.length === 0 ? (
          <p className="text-muted mt-0 text-sm">{es.calendario.puestoVacio}</p>
        ) : (
          <>
            <p className="text-muted mt-0 mb-3 text-sm">{es.calendario.puestoAyuda}</p>
            <ul className="grid list-none gap-2 p-0">
              {puestos.map((evento, index) =>
                evento.type === 'DayRangeDeclared' ? (
                  <li
                    key={`${evento.payload.from}-${String(index)}`}
                    className="border-rule m-0 flex flex-wrap items-baseline gap-x-3 rounded-lg border p-3 text-sm"
                  >
                    <span className="font-bold">
                      {evento.payload.note ?? es.days[evento.payload.kind]}
                    </span>
                    <span className="text-muted">
                      {evento.payload.from === evento.payload.to
                        ? enCorto(evento.payload.from)
                        : es.calendario.rango(
                            enCorto(evento.payload.from),
                            enCorto(evento.payload.to),
                          )}
                    </span>
                    <span className="text-teal ml-auto font-bold">
                      {es.days[evento.payload.kind]}
                    </span>
                  </li>
                ) : null,
              )}
            </ul>
          </>
        )}
      </section>
    </div>
  )
}
